import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { TIDS } from "@/constants/testIds";
import { toast } from "sonner";
import { Check, Sparkles, Building2, Globe2, TrendingUp, Infinity as InfinityIcon } from "lucide-react";

const ICONS = { free: Sparkles, pro: Globe2, business: Building2 };

// Human labels for each usage row
const USAGE_ROWS = [
  { key: "webinars_this_month", limit: "webinars_per_month", label: "Webinars this month", kind: "monthly" },
  { key: "ai_generations_this_month", limit: "ai_generations_per_month", label: "AI generations this month", kind: "monthly" },
  { key: "landing_pages_total", limit: "landing_pages_total", label: "Landing pages", kind: "total" },
  { key: "custom_domains_total", limit: "custom_domains_total", label: "Custom domains", kind: "total" },
  { key: "team_seats_total", limit: "team_seats_total", label: "Team members", kind: "total" },
];

const FEATURE_ROWS = [
  { key: "courses_enabled", label: "Courses & Cohorts" },
  { key: "whatsapp_priority", label: "Priority WhatsApp reminders" },
  { key: "white_label", label: "White-label branding" },
];

function UsageBar({ label, current, limit }) {
  const unlimited = limit === null || limit === undefined;
  const disabled = limit === false;
  const pct = unlimited || disabled ? 0 : Math.min(100, Math.round((current / Math.max(limit, 1)) * 100));
  const barColor = pct >= 100 ? "#B00020" : pct >= 80 ? "#D97706" : "#0B5CFF";
  return (
    <div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-[#0B0D17] font-medium">{label}</span>
        <span className={"font-mono text-xs " + (pct >= 100 ? "text-[#B00020] font-semibold" : "text-[#4A4D57]")}>
          {disabled ? "Not available" : unlimited ? (
            <span className="inline-flex items-center gap-1"><InfinityIcon className="w-3 h-3" /> unlimited</span>
          ) : `${current} / ${limit}`}
        </span>
      </div>
      {!unlimited && !disabled && (
        <div className="mt-1.5 h-1.5 rounded-full bg-[#E4E6EC] overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: barColor }} />
        </div>
      )}
    </div>
  );
}

export default function Plans() {
  const [current, setCurrent] = useState(null); // /plans/current shape
  const [limitsData, setLimitsData] = useState(null); // /plans/limits shape
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const [{ data: c }, { data: l }] = await Promise.all([
        api.get("/plans/current"),
        api.get("/plans/limits"),
      ]);
      setCurrent(c);
      setLimitsData(l);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  useEffect(() => { load(); }, []);

  const choose = async (plan) => {
    if (current?.plan === plan) return;
    setBusy(plan);
    try {
      await api.post("/plans/upgrade", { plan });
      toast.success(`Switched to ${plan} plan`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(null); }
  };

  if (!current || !limitsData) return <div className="text-[#4A4D57]">Loading…</div>;

  const order = ["free", "pro", "business"];
  return (
    <div className="space-y-8 vp-fade-up">
      <div>
        <div className="vp-label">Plans & billing</div>
        <h1 className="font-display text-3xl font-bold mt-1">Pick a plan for your workspace</h1>
        <p className="text-[#4A4D57] mt-2 max-w-xl">
          Current plan: <span className="font-semibold text-[#0B0D17]" data-testid={TIDS.currentPlanBadge}>{limitsData.label}</span>
          {limitsData.lifetime && <span className="ml-2 vp-chip vp-chip-accent" style={{ background: "#F59E0B22", color: "#B45309" }}>Lifetime</span>}.
        </p>
      </div>

      {/* Live usage panel */}
      <div className="vp-card p-5 sm:p-6" data-testid="plan-usage-panel">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-4 h-4 text-[#0B5CFF]" strokeWidth={1.75} />
          <div className="font-display font-semibold">Your usage this month</div>
          <span className="text-xs text-[#747680]">· {limitsData.usage.month}</span>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {USAGE_ROWS.map((row) => (
            <UsageBar
              key={row.key}
              label={row.label}
              current={limitsData.usage[row.key] || 0}
              limit={limitsData.limits[row.limit]}
            />
          ))}
        </div>
        <div className="mt-5 pt-4 border-t border-[#E4E6EC] grid sm:grid-cols-3 gap-3 text-sm">
          {FEATURE_ROWS.map((f) => {
            const enabled = !!limitsData.limits[f.key];
            return (
              <div key={f.key} className="flex items-center gap-2">
                {enabled ? (
                  <Check className="w-4 h-4 text-[#1E8E3E]" strokeWidth={2} />
                ) : (
                  <span className="w-4 h-4 rounded-full border border-[#E4E6EC] flex-shrink-0" />
                )}
                <span className={enabled ? "text-[#0B0D17]" : "text-[#747680] line-through"}>{f.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Plan tiers */}
      <div className="grid md:grid-cols-3 gap-5">
        {order.map((key) => {
          const p = current.all_plans[key];
          const Icon = ICONS[key];
          const isCurrent = current.plan === key;
          return (
            <div key={key} data-testid={TIDS.planCard + "-" + key}
              className={"vp-glass p-6 flex flex-col " + (isCurrent ? "border-[#0B5CFF] shadow-[0_8px_28px_rgba(11,92,255,0.12)]" : "")}>
              <div className="flex items-center gap-2">
                <Icon className="w-5 h-5 text-[#0B5CFF]" strokeWidth={1.5} />
                <div className="font-display font-bold text-xl">{p.label}</div>
                {isCurrent && <span className="ml-auto vp-chip vp-chip-accent">Current</span>}
              </div>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-display text-4xl font-black">
                  {p.price_inr_month === 0 ? "Free" : `₹${p.price_inr_month.toLocaleString("en-IN")}`}
                </span>
                {p.price_inr_month > 0 && <span className="text-[#747680] text-sm">/mo</span>}
              </div>
              <ul className="mt-5 space-y-2.5 text-sm flex-1">
                {p.perks.map((perk, i) => (
                  <li key={i} className="flex items-start gap-2 text-[#0B0D17]">
                    <Check className="w-4 h-4 text-[#1E8E3E] mt-0.5" strokeWidth={2} />
                    <span>{perk}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => choose(key)}
                disabled={isCurrent || busy === key || limitsData.lifetime}
                data-testid={TIDS.planSelectBtn + "-" + key}
                className={"mt-6 " + (isCurrent ? "vp-btn-ghost" : "vp-btn-primary")}
                title={limitsData.lifetime ? "You have a lifetime plan — no need to switch" : undefined}
              >
                {isCurrent ? "Current plan" : (busy === key ? "Switching…" : `Choose ${p.label}`)}
              </button>
            </div>
          );
        })}
      </div>

      <div className="text-xs text-[#747680] max-w-2xl">
        MVP note: Plan switching is instant and free during beta. Real billing (Razorpay subscriptions) will be enabled once payment keys are added.
      </div>
    </div>
  );
}
